import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  Check,
  ChevronDown,
  Compass,
  ExternalLink,
  History,
  Leaf,
  ShieldCheck,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/Button";
import { Progress } from "../components/Progress";
import { CompanionOverlay } from "../components/CompanionOverlay";
import {
  RodCountPicker,
  preferredRodCount,
  rememberRodCount,
} from "../components/RodCountPicker";
import {
  formatAdvisorDate,
  formatAdvisorValue,
} from "../../../../lib/carpintel-advisor";

// ─── helpers ──────────────────────────────────────────────────────────────────

type ConfidenceLevel =
  | "No evidence"
  | "Low Confidence"
  | "Medium Confidence"
  | "High Confidence";

function confidenceClass(level: ConfidenceLevel): string {
  if (level === "High Confidence") return "bg-success-weak text-success";
  if (level === "Medium Confidence") return "bg-info-weak text-info";
  if (level === "Low Confidence") return "bg-warning-weak text-warning";
  return "bg-inset text-secondary"; // No evidence
}

function scoreProgressVariant(
  score: number | null,
  level: ConfidenceLevel,
): "default" | "success" | "warning" | "error" {
  if (score === null || level === "No evidence") return "default";
  if (score >= 65) return "success";
  if (score >= 35) return "warning";
  return "default";
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdvisorResultsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [startingSession, setStartingSession] = useState(false);
  const [startSessionError, setStartSessionError] = useState("");
  const [rodCount, setRodCount] = useState(() => preferredRodCount());
  const query = useQuery({
    queryKey: ["carpintel_advisor_recommendation", id],
    queryFn: () =>
      fetch(`/app-api/advisor/recommendations/${id}`).then((response) => {
        if (!response.ok) throw new Error("Advice not found");
        return response.json();
      }),
  });

  if (query.isLoading)
    return <PageMessage text="Building your fishing advice…" />;
  if (query.error || !query.data)
    return (
      <PageMessage text="That advice could not be found." link="/advisor" />
    );

  const advice = query.data;
  const explanations: Record<string, string> =
    typeof advice.explanation_json === "string"
      ? JSON.parse(advice.explanation_json || "{}")
      : advice.explanation_json || {};
  const scoutSelection: {
    fixedSwimName?: string;
    observationLabels?: string[];
    scoutConfidence?: string;
  } | null = (() => {
    try {
      if (!explanations.scoutSelection) return null;
      return JSON.parse(explanations.scoutSelection);
    } catch {
      return null;
    }
  })();
  const provenance: Record<string, string> = (() => {
    try {
      return typeof advice.evidence_provenance_json === "string"
        ? JSON.parse(advice.evidence_provenance_json || "{}")
        : advice.evidence_provenance_json || {};
    } catch {
      return {};
    }
  })();
  const crossWaterScore = provenance.score === "cross-water";
  const incidentalOnlyScore =
    provenance.score === "same-lake" &&
    Number(advice.personal_session_count ?? 0) +
      Number(advice.community_session_count ?? 0) ===
      0 &&
    Number(advice.evidence_catch_count ?? 0) > 0;
  const compactFirstSentence = (text?: string | null) => {
    if (!text) return undefined;
    const match = text.trim().match(/^.*?[.!?](?:\s|$)/);
    return match?.[0]?.trim() || text.trim();
  };
  const compactEvidenceSummary = (
    source: string | undefined,
    fallback?: string | null,
  ) => {
    if (source === "same-lake")
      return "From successful fishing recorded on this lake.";
    if (source === "cross-water")
      return "From successful comparable fishing on other waters.";
    if (source === "external-lake")
      return "From recent public reports for this lake.";
    return compactFirstSentence(fallback);
  };

  const level: ConfidenceLevel = advice.confidence_level ?? "No evidence";
  const noEvidence = level === "No evidence";
  // hasScore: true when there is a meaningful stored score to display.
  // External-only fallback produces a score with evidence_session_count=0;
  // we therefore also show the score when fishing_score > 0 and confidence
  // is not "No evidence", regardless of session count.
  // hasScore: show score when confidence is set (not "No evidence") and a
  // non-zero score is stored. External-only fallback has evidence_session_count=0
  // but still stores a valid score — the session count guard is dropped so
  // external-only results display correctly.
  const hasScore = !noEvidence && Number(advice.fishing_score) > 0;
  const score = hasScore ? Number(advice.fishing_score) : null;
  const hasRecommendationPlan = Boolean(
    advice.recommended_area ||
      advice.best_bite_period ||
      advice.recommended_rig ||
      advice.recommended_main_bait ||
      advice.recommended_hookbait ||
      advice.recommended_additive ||
      advice.recommended_bait_approach ||
      advice.recommended_tactic ||
      advice.alternative_tactic,
  );

  const hasForecast = [
    advice.forecast_air_temp_c,
    advice.forecast_pressure_hpa,
    advice.forecast_pressure_trend,
    advice.forecast_wind_direction,
    advice.forecast_wind_speed_mph,
    advice.forecast_cloud_pct,
    advice.forecast_precipitation_mm,
  ].some((v) => v !== null && v !== undefined && v !== "");

  // Fix 4: handle Start session from advice.
  // When advice is a Future Trip and the canonical lake has no personal lake_id,
  // first add it to My Lakes, then navigate with the resolved lake_id.
  async function handleStartSession(e: React.MouseEvent) {
    e.preventDefault();
    setStartSessionError("");

    const needsLakeAdd =
      advice.is_future_trip &&
      !advice.lake_id &&
      advice.future_trip_canonical_lake_id;

    setStartingSession(true);
    try {
      // For Future Trip advice where the lake is not yet in My Lakes,
      // resolve a personal lake_id first.
      let resolvedLakeId: number | undefined;
      if (needsLakeAdd) {
        const lakeRes = await fetch(
          "/app-api/advisor/ensure-lake-in-my-lakes",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              canonical_lake_id: advice.future_trip_canonical_lake_id,
            }),
          },
        );
        const lakeData = await lakeRes.json();
        if (!lakeRes.ok || !lakeData.id)
          throw new Error(lakeData.error || "Could not add lake");
        await queryClient.invalidateQueries({ queryKey: ["carpintel_lakes"] });
        resolvedLakeId = lakeData.id;
      }

      // Build the session payload from advice data — same field mapping as
      // buildStartSessionUrl / StartSession form, reused here directly.
      const effectiveLakeId = resolvedLakeId ?? advice.lake_id ?? null;
      if (!effectiveLakeId)
        throw new Error("No lake selected for this advice.");

      // Derive start_at: fishing_date + expected_start_time, or now.
      let startAt: string;
      if (advice.fishing_date && advice.expected_start_time) {
        const hm = String(advice.expected_start_time).slice(0, 5);
        // Construct as a local datetime-local string then convert via Date.
        startAt = new Date(
          `${String(advice.fishing_date).slice(0, 10)}T${hm}`,
        ).toISOString();
      } else {
        startAt = new Date().toISOString();
      }

      // Map pressure_trend (same logic as StartSession initialFromUrl).
      const rawTrend = String(advice.forecast_pressure_trend ?? "").trim();
      const pressureTrend = ["rising", "falling", "stable"].includes(rawTrend)
        ? rawTrend
        : "not-recorded";

      // rain_observed heuristic from precipitation.
      const precipMm = advice.forecast_precipitation_mm;
      const rainObserved =
        precipMm !== null && precipMm !== undefined
          ? Number(precipMm) > 0
            ? "true"
            : "false"
          : "not-recorded";

      const payload = {
        lake_id: Number(effectiveLakeId),
        peg: advice.recommended_area || "",
        start_at: startAt,
        end_at: null, // always live on creation
        rods_count: rodCount,
        target_species: advice.target_species || "both",
        weather: "",
        rain_observed: rainObserved,
        pressure_trend: pressureTrend,
        air_temp_c: advice.forecast_air_temp_c ?? null,
        wind_direction: advice.forecast_wind_direction ?? null,
        wind_speed_mph: advice.forecast_wind_speed_mph ?? null,
        air_pressure_hpa: advice.forecast_pressure_hpa ?? null,
        cloud_cover_pct: advice.forecast_cloud_pct ?? null,
        water_temp_c: null,
        gps_latitude: null,
        gps_longitude: null,
        privacy_setting: "Contribute anonymously to CarpIntel statistics",
        share_peg_anonymously: false,
        notes: "",
        advisor_recommendation_id: advice.id,
      };

      const res = await fetch("/app-api/sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.id)
        throw new Error(data.error || "Could not start session");

      rememberRodCount(rodCount);
      queryClient.invalidateQueries({ queryKey: ["carpintel_sessions"] });
      navigate(`/sessions/${data.id}`);
    } catch (err) {
      setStartSessionError(
        err instanceof Error ? err.message : "Could not start session",
      );
    } finally {
      setStartingSession(false);
    }
  }

  return (
    <div className="mx-auto max-w-[1200px] space-y-6 px-4 py-6 pb-28 md:py-8 md:pb-10">
      <header>
        <Link
          to="/advisor"
          className="text-sm font-medium text-accent hover:underline"
        >
          CarpIntel Advisor
        </Link>
        <div className="mt-2 flex min-w-0 items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm text-secondary">
              {advice.future_trip_lake_name || advice.lake_name} ·{" "}
              {formatAdvisorDate(advice.trip_date || advice.fishing_date)}
              {advice.target_species ? (
                <span className="ml-2 inline-flex items-center rounded-full bg-inset px-2 py-0.5 text-xs font-medium capitalize text-secondary">
                  {advice.target_species}
                </span>
              ) : null}
              {advice.is_future_trip ? (
                <span className="ml-1 inline-flex items-center rounded-full bg-info-weak px-2 py-0.5 text-xs font-medium text-info">
                  Future trip
                </span>
              ) : null}
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-primary">
              Your fishing advice
            </h1>
            <p className="mt-1 text-sm text-secondary">
              Start {String(advice.expected_start_time).slice(0, 5)} for{" "}
              {advice.expected_duration_hours}{" "}
              {Number(advice.expected_duration_hours) === 1 ? "hour" : "hours"}.
            </p>
          </div>
          <Button asChild variant="secondary">
            <Link to="/advisor">
              <History /> New advice
            </Link>
          </Button>
        </div>
      </header>

      {(!noEvidence || hasRecommendationPlan) ? (
        <section className="space-y-3">
          <StartingPlan advice={advice} />
        </section>
      ) : null}

      {/* ── Score and evidence panel ── */}
      <section className="grid grid-cols-1 gap-3 md:grid-cols-[1.1fr_0.9fr]">
        <div className="rounded-lg border border-border bg-raised p-5">
          <div className="flex min-w-0 items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm text-secondary">Fishing score</p>
              {noEvidence ? (
                <p className="mt-1 text-2xl font-semibold text-secondary">
                  No score yet
                </p>
              ) : (
                <p className="mt-1 text-5xl font-semibold tracking-tight text-primary">
                  {score}
                  <span className="text-lg text-secondary">/100</span>
                </p>
              )}
            </div>
            <span
              className={`mt-1 inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${confidenceClass(level)}`}
            >
              {noEvidence && Number(advice.external_evidence_count) > 0
                ? "Insufficient evidence"
                : level}
            </span>
          </div>

          {noEvidence ? (
            <p className="mt-4 text-sm leading-6 text-secondary">
              Not enough evidence yet to calculate a Fishing Score.
            </p>
          ) : (
            <>
              <Progress
                className="mt-5"
                value={score ?? 0}
                variant={scoreProgressVariant(score, level)}
                aria-label={`Fishing score ${score} out of 100`}
              />
              <p className="mt-3 text-sm leading-6 text-secondary">
                Fishing Score is a prediction score (not the catch rate).
              </p>
            </>
          )}
        </div>

        <div className="rounded-lg border border-border bg-raised p-5">
          <div className="flex items-center gap-2">
            <ShieldCheck className="text-secondary" />
            <h2 className="text-base font-semibold text-primary">
              {crossWaterScore ? "Evidence from other waters" : "Evidence from this lake"}
            </h2>
          </div>
          <p className="mt-4 text-xs leading-5 text-secondary">
            {incidentalOnlyScore
              ? `${advice.evidence_catch_count ?? 0} same-lake catch${Number(advice.evidence_catch_count ?? 0) === 1 ? "" : "es"} · no targeted ${advice.target_species || "species"} session yet`
              : `${advice.personal_session_count ?? 0} personal · ${advice.community_session_count ?? 0} community · ${advice.external_evidence_count ?? 0} external · ${advice.evidence_catch_count ?? 0} comparable catches`}
          </p>
          <p className="mt-2 text-xs leading-5 text-tertiary">
            {crossWaterScore
              ? "There was not enough scoring evidence from this lake, so the Fishing Score uses comparable sessions from other waters. Location and bite-time patterns are only used when they come from this lake."
              : incidentalOnlyScore
                ? "These catches were recorded on this lake during sessions targeting another species. They give the Fishing Score a reduced positive signal, but those sessions are not counted as target-species blanks or in the target-species catch rate."
                : "The Fishing Score uses targeted sessions from this lake. Successful incidental catches can also add a smaller positive signal without turning other-species sessions into blanks."}
          </p>
        </div>
      </section>

      {/* ── Future Trip banner ── */}
      {advice.is_future_trip && (
        <div className="rounded-md border border-info bg-info-weak p-4 text-sm text-info">
          <strong className="text-primary">Future Trip mode.</strong>{" "}
          {explanations.futureTrip ||
            "Weather similarity scoring is paused. Evidence is based on seasonal patterns and available lake history. Detailed weather forecast will become available closer to the trip."}
        </div>
      )}

      {/* ── Recent external evidence items ── */}
      {(() => {
        const items: any[] = (() => {
          try {
            return typeof advice.external_evidence_json === "string"
              ? JSON.parse(advice.external_evidence_json || "[]")
              : advice.external_evidence_json || [];
          } catch {
            return [];
          }
        })();
        if (!items.length) return null;
        return (
          <section className="space-y-3">
            <h2 className="text-base font-semibold text-primary">
              Recent lake activity (external reports)
            </h2>
            <div className="space-y-2">
              {items.map((item: any, i: number) => (
                <div
                  key={i}
                  className="rounded-lg border border-border bg-raised p-3 text-sm"
                >
                  <div className="flex min-w-0 items-start justify-between gap-2">
                    <div className="min-w-0 space-y-0.5">
                      {item.date ? (
                        <p className="text-xs text-secondary">
                          {formatAdvisorDate(item.date)}
                          {item.date_precision &&
                          item.date_precision !== "exact"
                            ? ` (${item.date_precision})`
                            : ""}
                          {item.species_scope ? ` · ${item.species_scope}` : ""}
                        </p>
                      ) : null}
                      {item.landed_count || item.largest_weight_text ? (
                        <p className="font-medium text-primary">
                          {item.landed_count ? `${item.landed_count} fish` : ""}
                          {item.landed_count && item.largest_weight_text
                            ? " · "
                            : ""}
                          {item.largest_weight_text
                            ? `to ${item.largest_weight_text}`
                            : ""}
                        </p>
                      ) : null}
                      {item.bait_raw || item.rig_raw || item.tactic_raw ? (
                        <p className="text-xs text-secondary">
                          {[item.bait_raw, item.rig_raw, item.tactic_raw]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      ) : null}
                      {item.source_publisher ? (
                        <p className="text-xs text-secondary">
                          {item.source_publisher}
                        </p>
                      ) : null}
                    </div>
                    {item.source_url ? (
                      <a
                        href={item.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 text-accent"
                        aria-label="Source report"
                      >
                        <ExternalLink size={14} />
                      </a>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-secondary">
              Public reports can add useful context, but they stay separate
              from your logged CatchCompass sessions.
            </p>
          </section>
        );
      })()}

      {scoutSelection ? (
        <section className="rounded-xl border border-accent/40 bg-raised p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-accent">
            Scouted peg
          </p>
          <p className="mt-1 text-lg font-semibold text-primary">
            {scoutSelection.fixedSwimName || advice.recommended_area}
          </p>
          {scoutSelection.observationLabels?.length ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {scoutSelection.observationLabels.map((label) => (
                <span
                  key={label}
                  className="rounded-full border border-border bg-background px-2.5 py-1 text-xs text-secondary"
                >
                  {label}
                </span>
              ))}
            </div>
          ) : null}
          <p className="mt-3 text-xs leading-5 text-secondary">
            These are your live observations from this peg. CatchCompass keeps them attached to the advice without pretending they are historical proof.
          </p>
        </section>
      ) : null}

      {/* ── Confidence banner ── */}
      {noEvidence && (
        <div className="rounded-md border border-border bg-inset p-4 text-sm text-secondary">
          <strong className="text-primary">
            Insufficient historical data.
          </strong>{" "}
          {hasRecommendationPlan
            ? "There is not enough evidence from this lake for a Fishing Score yet. Where useful, CatchCompass can still suggest bait, rigs and tactics that have worked in comparable fishing elsewhere."
            : explanations.confidence ||
              "Start logging sessions at this lake — including blank sessions — to build up evidence."}
        </div>
      )}
      {!noEvidence && level === "Low Confidence" && (
        <div className="rounded-md border border-warning bg-warning-weak p-4 text-sm text-warning">
          The evidence is still limited. Treat these recommendations as a starting point, not a proven pattern.
        </div>
      )}

      {/* ── Recommended plan ── */}
      {(!noEvidence || hasRecommendationPlan) && (
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Compass className="text-secondary" />
            <h2 className="text-lg font-semibold text-primary">
              Plan details
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            <Recommendation
              label="Peg / swim"
              value={advice.recommended_area}
              qualifier={advice.area_qualifier}
              explanation={compactEvidenceSummary(provenance.area, explanations.area)}
              lesson={explanations.lessonArea}
              limit={explanations.limitArea}
              patternStatus={explanations.patternStatusArea}
              whyToday={explanations.whyTodayArea}
            />
            <Recommendation
              label="Best predicted bite period"
              value={advice.best_bite_period}
              qualifier={advice.bite_period_qualifier}
              explanation={compactEvidenceSummary(provenance.bitePeriod, explanations.bitePeriod)}
              lesson={explanations.lessonBitePeriod}
              limit={explanations.limitBitePeriod}
              patternStatus={explanations.patternStatusBitePeriod}
              whyToday={explanations.whyTodayBitePeriod}
            />
            <Recommendation
              label="Rig"
              value={advice.recommended_rig}
              qualifier={advice.rig_qualifier}
              explanation={compactEvidenceSummary(provenance.rig, explanations.rig)}
              lesson={explanations.lessonRig}
              limit={explanations.limitRig}
              patternStatus={explanations.patternStatusRig}
              whyToday={explanations.whyTodayRig}
            />
            <Recommendation
              label="Main bait"
              value={advice.recommended_main_bait}
              qualifier={advice.main_bait_qualifier}
              explanation={compactEvidenceSummary(provenance.mainBait, explanations.mainBait)}
              lesson={explanations.lessonMainBait}
              limit={explanations.limitMainBait}
              patternStatus={explanations.patternStatusMainBait}
              whyToday={explanations.whyTodayMainBait}
            />
            <Recommendation
              label="Hookbait"
              value={advice.recommended_hookbait}
              qualifier={advice.hookbait_qualifier}
              explanation={compactEvidenceSummary(provenance.hookbait, explanations.hookbait)}
              lesson={explanations.lessonHookbait}
              limit={explanations.limitHookbait}
              patternStatus={explanations.patternStatusHookbait}
              whyToday={explanations.whyTodayHookbait}
            />
            <Recommendation
              label="Attractor / additive"
              value={advice.recommended_additive}
              qualifier={advice.additive_qualifier}
              explanation={compactEvidenceSummary(provenance.additive, explanations.additive)}
              lesson={explanations.lessonAdditive}
              limit={explanations.limitAdditive}
              patternStatus={explanations.patternStatusAdditive}
              whyToday={explanations.whyTodayAdditive}
            />
            <Recommendation
              label="Feeding approach"
              value={advice.recommended_bait_approach}
              qualifier={advice.bait_approach_qualifier}
              explanation={compactEvidenceSummary(provenance.baitApproach, explanations.baitAmount)}
              lesson={explanations.lessonBaitAmount}
              limit={explanations.limitBaitAmount}
              patternStatus={explanations.patternStatusBaitApproach}
              whyToday={explanations.whyTodayBaitApproach}
            />
            <Recommendation
              label="Suggested tactic"
              value={advice.recommended_tactic}
              qualifier={advice.tactic_qualifier}
              explanation={compactEvidenceSummary(provenance.tactic, explanations.tactic)}
              lesson={explanations.lessonTactic}
              limit={explanations.limitTactic}
              patternStatus={explanations.patternStatusTactic}
              whyToday={explanations.whyTodayTactic}
            />
          </div>
        </section>
      )}

      {(advice.recommended_depth_ft != null ||
        advice.recommended_distance_yds != null ||
        advice.recommended_bait_amount != null) && (
        <section className="rounded-lg border border-border bg-inset p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-secondary">
            Context signals, not targets
          </p>
          <p className="mt-2 text-sm leading-6 text-secondary">
            CatchCompass records depth, range and feed quantity because they help explain
            patterns. They are deliberately not treated as fixed recommendations because
            the lake and the fish change through the season and during a session.
          </p>
          <div className="mt-3 space-y-3">
            {advice.recommended_depth_ft != null ? (
              <div>
                <p className="text-sm font-semibold text-primary">
                  Depth context · {formatAdvisorValue(advice.recommended_depth_ft, "ft")}
                </p>
                <p className="mt-1 text-xs leading-5 text-secondary">
                  {explanations.lessonDepth || explanations.depth}
                </p>
              </div>
            ) : null}
            {advice.recommended_distance_yds != null ? (
              <div>
                <p className="text-sm font-semibold text-primary">
                  Range context · {formatAdvisorValue(advice.recommended_distance_yds, "yds")}
                </p>
                <p className="mt-1 text-xs leading-5 text-secondary">
                  {explanations.lessonDistance || explanations.distance}
                </p>
              </div>
            ) : null}
            {advice.recommended_bait_amount != null ? (
              <div>
                <p className="text-sm font-semibold text-primary">Feed quantity context</p>
                <p className="mt-1 text-xs leading-5 text-secondary">
                  {explanations.lessonBaitAmount || explanations.baitAmount}
                </p>
              </div>
            ) : null}
          </div>
        </section>
      )}

      {/* ── Natural Food card ── */}
      <NaturalFoodCard explanations={explanations} />

      {/* ── Alternative tactic + Start session ── */}
      <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {(!noEvidence || hasRecommendationPlan) && (
          <div className="rounded-lg border border-border bg-raised p-4">
            <p className="text-sm text-secondary">
              Second-choice evidence-backed option
            </p>
            <p className="mt-2 text-base font-semibold text-primary">
              {advice.alternative_tactic || "No supported alternative yet"}
            </p>
            <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-secondary">
              Evidence
            </p>
            <p className="mt-1 text-sm leading-6 text-secondary">
              {compactFirstSentence(explanations.alternative) ||
                "A second distinct tactic needs more comparable catches."}
            </p>
            {explanations.lessonAlternative ? (
              <>
                <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-accent">
                  What to learn
                </p>
                <p className="mt-1 text-sm leading-6 text-primary">
                  {explanations.lessonAlternative}
                </p>
              </>
            ) : null}
            {explanations.limitAlternative ? (
              <div className="mt-3 rounded-md bg-inset p-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-secondary">
                  Don't overlearn it
                </p>
                <p className="mt-1 text-xs leading-5 text-secondary">
                  {explanations.limitAlternative}
                </p>
              </div>
            ) : null}
          </div>
        )}
        <div className="rounded-lg border border-border bg-raised p-4">
          {advice.actual_session_id ? (
            <>
              <p className="text-sm text-secondary">
                Today's advice for your live session
              </p>
              <p className="mt-2 text-base font-semibold text-primary">
                Refreshed with the latest available conditions
              </p>
              <p className="mt-2 text-sm leading-6 text-secondary">
                This recommendation is already linked to your active session.
                Keep logging catches there as conditions change.
              </p>
              <Button asChild size="lg" className="mt-4 w-full sm:w-auto">
                <Link to={`/sessions/${advice.actual_session_id}`}>
                  Back to live session <ArrowRight />
                </Link>
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-secondary">
                Start a session from this advice
              </p>
              <p className="mt-2 text-base font-semibold text-primary">
                {hasForecast
                  ? "Pre-filled with your plan and forecast"
                  : "Pre-filled with your trip plan"}
              </p>
              <p className="mt-2 text-sm leading-6 text-secondary">
                The session will be linked to this recommendation so CarpIntel can
                compare the prediction against what actually happened.
              </p>
              <div className="mt-4">
                <RodCountPicker
                  value={rodCount}
                  onChange={setRodCount}
                  disabled={startingSession}
                />
              </div>
              {startSessionError ? (
                <p className="mt-2 text-sm text-error" role="alert">
                  {startSessionError}
                </p>
              ) : null}
              <Button
                size="lg"
                className="mt-4 w-full sm:w-auto"
                isLoading={startingSession}
                onClick={handleStartSession}
              >
                Start session from advice <ArrowRight />
              </Button>
            </>
          )}
        </div>
      </section>

      {/* ── Forecast snapshot ── */}
      {hasForecast ? (
        <section className="rounded-lg border border-border bg-raised p-4">
          <h2 className="text-base font-semibold text-primary">
            Forecast snapshot used for this advice
          </h2>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <Snapshot
              label="Air temp"
              value={
                advice.forecast_air_temp_c != null
                  ? `${advice.forecast_air_temp_c}°C`
                  : null
              }
            />
            <Snapshot
              label="Pressure"
              value={
                advice.forecast_pressure_hpa != null
                  ? `${advice.forecast_pressure_hpa} hPa`
                  : null
              }
            />
            <Snapshot
              label="Pressure trend"
              value={advice.forecast_pressure_trend}
            />
            <Snapshot
              label="Wind"
              value={
                advice.forecast_wind_direction
                  ? `${advice.forecast_wind_direction} ${advice.forecast_wind_speed_mph != null ? `${advice.forecast_wind_speed_mph} mph` : ""}`
                  : null
              }
            />
            <Snapshot
              label="Cloud"
              value={
                advice.forecast_cloud_pct != null
                  ? `${advice.forecast_cloud_pct}%`
                  : null
              }
            />
            <Snapshot
              label="Precipitation"
              value={
                advice.forecast_precipitation_mm != null
                  ? `${advice.forecast_precipitation_mm} mm`
                  : null
              }
            />
          </div>
        </section>
      ) : (
        <p className="text-sm text-secondary">
          {Number(advice.evidence_session_count) > 0 ||
          Number(advice.external_evidence_count) > 0
            ? "No forecast values were supplied. Advice is based on seasonal timing and available lake evidence."
            : "No forecast values were supplied. No comparable lake history is currently available."}
        </p>
      )}

      {/* ── Linked session confirmation ── */}
      {advice.actual_session_id ? (
        <div className="rounded-md bg-success-weak p-4 text-sm text-success">
          <Check className="mr-2 inline size-4" />
          Linked to actual session {advice.actual_session_id} for later
          comparison.
        </div>
      ) : null}

      <CompanionOverlay
        context={{ type: "advisor", recommendationIds: [advice.id] }}
      />
    </div>
  );
}

// ─── Helper: pure string arithmetic for adding hours to a date+time pair ─────
// Never passes a bare "YYYY-MM-DDTHH:MM" string to `new Date()` because that
// is parsed as LOCAL time in all major browsers, causing a timezone shift when
// the browser is not in UTC. All date rolling uses UTC noon as an anchor so
// DST gaps cannot swallow or duplicate a day.
function addHoursToPlannedDateTime(
  dateStr: string,
  hmStr: string,
  durationHours: number,
): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const [y, mo, d] = dateStr.split("-").map(Number);
  const [h, mi] = hmStr.split(":").map(Number);
  const startMins = h * 60 + mi;
  const totalEndMins = startMins + Math.round(durationHours * 60);
  const extraDays = Math.floor(totalEndMins / 1440);
  const endH = Math.floor((totalEndMins % 1440) / 60);
  const endMi = totalEndMins % 60;
  // Roll date forward via UTC noon — avoids DST boundary issues
  const anchor = new Date(Date.UTC(y, mo - 1, d + extraDays, 12, 0, 0));
  const ey = anchor.getUTCFullYear();
  const em = anchor.getUTCMonth() + 1;
  const ed = anchor.getUTCDate();
  return `${ey}-${pad(em)}-${pad(ed)}T${pad(endH)}:${pad(endMi)}`;
}

// ─── Helper: build the start-session URL with all advisor fields prefilled ───

// resolvedLakeId: a personal lake_id obtained after ensure-lake-in-my-lakes.
// When provided it overrides advice.lake_id (used for Future Trip auto-add).
function buildStartSessionUrl(
  advice: Record<string, any>,
  resolvedLakeId?: number,
): string {
  const params = new URLSearchParams();
  params.set("advisorId", String(advice.id));
  const effectiveLakeId = resolvedLakeId ?? advice.lake_id ?? null;
  if (effectiveLakeId) params.set("lake_id", String(effectiveLakeId));
  if (advice.recommended_area) params.set("peg", advice.recommended_area);

  // expected_start_time from Postgres TIME column arrives as "HH:MM:SS".
  // Slice to "HH:MM" and combine with the stored fishing_date (plain string,
  // never passed through new Date() to avoid timezone shift).
  if (advice.fishing_date && advice.expected_start_time) {
    const hm = String(advice.expected_start_time).slice(0, 5); // "05:30"
    const startDTL = `${String(advice.fishing_date).slice(0, 10)}T${hm}`;
    params.set("start_at", startDTL);

    if (advice.expected_duration_hours) {
      const endDTL = addHoursToPlannedDateTime(
        String(advice.fishing_date).slice(0, 10),
        hm,
        Number(advice.expected_duration_hours),
      );
      params.set("end_at", endDTL);
    }
  }

  // Forecast fields — sourced exclusively from THIS recommendation's stored
  // values. Guard against null AND empty string (Postgres returns "" for
  // varchar columns saved with an empty string).
  const hasValue = (v: unknown) =>
    v !== null && v !== undefined && String(v).trim() !== "";

  if (hasValue(advice.forecast_air_temp_c))
    params.set("air_temp_c", String(advice.forecast_air_temp_c));
  if (hasValue(advice.forecast_pressure_hpa))
    params.set("air_pressure_hpa", String(advice.forecast_pressure_hpa));
  if (hasValue(advice.forecast_pressure_trend))
    params.set("pressure_trend", String(advice.forecast_pressure_trend).trim());
  if (hasValue(advice.forecast_wind_direction))
    params.set("wind_direction", String(advice.forecast_wind_direction).trim());
  if (hasValue(advice.forecast_wind_speed_mph))
    params.set("wind_speed_mph", String(advice.forecast_wind_speed_mph));
  if (hasValue(advice.forecast_cloud_pct))
    params.set("cloud_cover_pct", String(advice.forecast_cloud_pct));
  if (hasValue(advice.forecast_precipitation_mm))
    params.set("precipitation_mm", String(advice.forecast_precipitation_mm));

  return `/sessions/new?${params.toString()}`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function anglerPatternLabel(status: string | null | undefined) {
  switch (String(status || "").toLowerCase()) {
    case "established":
      return "Strong pattern";
    case "supported":
      return "Worth trying";
    case "emerging":
      return "Early indication";
    case "changing":
      return "Pattern changing";
    case "contradicted":
      return "Evidence against";
    default:
      return "Fishing theory only";
  }
}

function Recommendation({
  label,
  value,
  qualifier,
  explanation,
  lesson,
  limit,
  patternStatus,
  whyToday,
}: {
  label: string;
  value: string | number | null | undefined;
  qualifier?: string | null;
  explanation?: string;
  lesson?: string | null;
  limit?: string | null;
  patternStatus?: string | null;
  whyToday?: string | null;
}) {
  const hasRecommendation =
    value !== null && value !== undefined && String(value).trim() !== "";

  return (
    <details className="group rounded-lg border border-border bg-raised">
      <summary className="flex min-h-20 cursor-pointer list-none items-center justify-between gap-3 p-4">
        <div className="min-w-0">
          <p className="text-sm text-secondary">{label}</p>
          <p className={`mt-1 text-base font-semibold ${hasRecommendation ? "text-primary" : "text-secondary"}`}>
            {hasRecommendation ? value : "Not enough evidence yet"}
          </p>
          {qualifier ? (
            <p className="mt-1 text-xs italic text-secondary">{qualifier}</p>
          ) : null}
          {hasRecommendation && patternStatus ? (
            <span className="mt-2 inline-flex rounded-full bg-inset px-2 py-0.5 text-[11px] font-semibold text-secondary">
              {anglerPatternLabel(patternStatus)}
            </span>
          ) : null}
        </div>
        <ChevronDown
          size={18}
          className="shrink-0 text-secondary transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>

      <div className="border-t border-border/70 px-4 pb-4 pt-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-secondary">
          Why this?
        </p>
        <p className="mt-1 text-sm leading-6 text-secondary">
          {explanation ||
            "CatchCompass does not have enough reliable evidence for this part of the plan yet."}
        </p>

        {whyToday ? (
          <div className="mt-3 rounded-md bg-inset p-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-accent">
              Why this fits today
            </p>
            <p className="mt-1 text-xs leading-5 text-secondary">{whyToday}</p>
          </div>
        ) : null}

        {lesson ? (
          <div className="mt-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-accent">
              What to learn
            </p>
            <p className="mt-1 text-sm leading-6 text-primary">{lesson}</p>
          </div>
        ) : null}

        {limit ? (
          <div className="mt-3 rounded-md bg-inset p-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-secondary">
              Don't overlearn it
            </p>
            <p className="mt-1 text-xs leading-5 text-secondary">{limit}</p>
          </div>
        ) : null}
      </div>
    </details>
  );
}

function StartingPlan({ advice }: { advice: any }) {
  const baiting = advice.recommended_bait_approach || null;
  const items = [
    ["Where", advice.recommended_area],
    ["Rig", advice.recommended_rig],
    ["Main bait", advice.recommended_main_bait],
    ["Hookbait", advice.recommended_hookbait],
    ["Baiting", baiting],
    ["Best window", advice.best_bite_period],
  ].filter(([, value]) => value !== null && value !== undefined && String(value).trim());

  if (!items.length) return null;

  return (
    <div className="rounded-xl border border-accent/40 bg-raised p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-accent">
        Your starting plan
      </p>
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
        {items.map(([label, value]) => (
          <div key={String(label)} className="min-w-0">
            <p className="text-xs text-secondary">{label}</p>
            <p className="mt-0.5 break-words text-sm font-semibold text-primary">
              {String(value)}
            </p>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs leading-5 text-secondary">
        Tap any recommendation below to see why CatchCompass suggested it and what you can learn from the pattern.
      </p>
    </div>
  );
}

function Snapshot({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div>
      <p className="text-xs text-secondary">{label}</p>
      <p className="mt-1 font-medium text-primary">{value || "Not provided"}</p>
    </div>
  );
}

// ─── Natural Food types (mirrors lib/carpintel-natural-food.ts) ─────────────
// Duplicated here to avoid importing lib code into client bundles.
type NaturalFoodEvidenceLabel = "Reported / Known" | "Likely" | "Possible";
type NaturalFoodItem = {
  name: string;
  label: NaturalFoodEvidenceLabel;
  note: string;
};
type NaturalFoodInsights = {
  items: NaturalFoodItem[];
  hasLakeSpecificObservations: boolean;
  season: string;
};

function evidencePillClass(label: NaturalFoodEvidenceLabel): string {
  if (label === "Reported / Known") return "bg-success-weak text-success";
  if (label === "Likely") return "bg-info-weak text-info";
  return "bg-inset text-secondary";
}

function NaturalFoodCard({
  explanations,
}: {
  explanations: Record<string, string>;
}) {
  const raw = explanations.naturalFood;
  if (!raw) return null;

  let insights: NaturalFoodInsights | null = null;
  try {
    insights = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!insights || !insights.items || insights.items.length === 0) return null;

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <Leaf className="text-secondary" size={18} />
        <h2 className="text-lg font-semibold text-primary">Natural food</h2>
        <span className="text-xs text-secondary">
          {insights.season} · watercraft context only
        </span>
      </div>

      <div className="rounded-lg border border-border bg-raised p-4 space-y-4">
        <p className="text-xs text-secondary leading-5">
          Natural food sources a carp may reasonably encounter at this time of
          year. This is ecological context only — it does not affect the Fishing
          Score, Confidence, or any recommendation.
        </p>

        <ul className="divide-y divide-border-weak">
          {insights.items.map((item, i) => (
            <li key={i} className="py-3 first:pt-0 last:pb-0">
              <div className="flex min-w-0 items-start justify-between gap-3">
                <p className="text-sm font-medium text-primary min-w-0">
                  {item.name}
                </p>
                <span
                  className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${evidencePillClass(
                    item.label,
                  )}`}
                >
                  {item.label}
                </span>
              </div>
              <p className="mt-1 text-xs leading-5 text-secondary">
                {item.note}
              </p>
            </li>
          ))}
        </ul>

        {!insights.hasLakeSpecificObservations && (
          <p className="text-xs text-secondary border-t border-border-weak pt-3">
            Lake-specific observations: None currently recorded. Items above are
            based on season alone.
          </p>
        )}
      </div>
    </section>
  );
}

function PageMessage({ text, link }: { text: string; link?: string }) {
  return (
    <div className="mx-auto max-w-[760px] px-4 py-8 text-center text-sm text-secondary">
      <p>{text}</p>
      {link ? (
        <Link
          to={link}
          className="mt-3 inline-block font-medium text-accent hover:underline"
        >
          Back to Advisor
        </Link>
      ) : null}
    </div>
  );
}

